import { Link } from "wouter";

import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE = "May 1, 2025";

const SECTIONS = [
  {
    title: "1. Service Description",
    content: `Zindeal ("Service") is a software-as-a-service platform that helps college and university athletic departments manage coaching contracts, insurance compliance, risk mitigation, and related administrative workflows. The Service is provided by Zebiso Intelligence, Inc. ("Company," "we," "us," or "our").

By creating an account or using the Service, you ("User," "Customer," or "you") agree to be bound by these Terms of Service ("Terms"). If you are accepting on behalf of a company or other legal entity, you represent that you have the authority to bind that entity to these Terms.`,
  },
  {
    title: "2. User Accounts",
    content: `To access the Service, you must create an account and provide accurate, current, and complete information. You are responsible for:

• Maintaining the confidentiality of your account credentials
• All activity that occurs under your account
• Promptly notifying us of any unauthorized use at support@zindeal.com

We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or pose a risk to the security of the Service or other users.`,
  },
  {
    title: "3. Subscription and Payment Terms",
    content: `The Service is offered on a subscription basis. By selecting a paid plan:

• You authorize us to charge your payment method on a recurring basis (monthly or annually, as selected) at the rates in effect at the time of purchase.
• All fees are exclusive of applicable taxes, which you are responsible for paying.
• Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period.
• 14-day free trials are available for Starter and Pro plans. No charge is made during the trial. You may cancel at any time during the trial without charge.
• We reserve the right to change pricing with 30 days' notice to existing customers.

All payments are processed securely through Stripe. We do not store your full payment card details.`,
  },
  {
    title: "4. Data Ownership",
    content: `You retain all rights, title, and interest in and to the data you upload, submit, or create within the Service ("Customer Data"). You grant us a limited, non-exclusive license to store and process Customer Data solely to provide and improve the Service.

We will not sell, share, or use Customer Data for any purpose other than operating the Service, except as required by law. Upon cancellation, you may export your data in supported formats for 90 days; after that period, we may delete Customer Data from our systems.`,
  },
  {
    title: "5. Acceptable Use",
    content: `You agree not to:

• Use the Service for any unlawful purpose or in violation of any applicable NCAA, NAIA, or other governing body regulations
• Upload malicious code, viruses, or any software designed to damage or interfere with the Service
• Attempt to gain unauthorized access to any part of the Service or its infrastructure
• Resell, sublicense, or otherwise provide access to the Service to third parties without written consent
• Use the Service to store or transmit personally identifiable information of minors in a manner that violates applicable law

We reserve the right to investigate and take appropriate action against violations, including suspension or termination of your account.`,
  },
  {
    title: "6. Intellectual Property",
    content: `The Service, including all software, algorithms, user interface designs, text, graphics, and other content created by us, is and remains the exclusive property of Zebiso Intelligence, Inc. and its licensors. These Terms do not grant you any right to use our trademarks, logos, or other brand features.

Feedback you provide about the Service may be used by us to improve the Service without any obligation to compensate you.`,
  },
  {
    title: "7. Confidentiality",
    content: `Each party agrees to keep confidential the other party's non-public business and technical information ("Confidential Information") and not to disclose it to third parties except as required to perform obligations under these Terms or as required by law. This obligation survives termination of these Terms for three years.

Customer Data is considered your Confidential Information. Our technical and business information shared with you in the course of providing the Service is our Confidential Information.`,
  },
  {
    title: "8. Disclaimer of Warranties",
    content: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE SPECIFICALLY DISCLAIM ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.

We do not warrant that the Service will be uninterrupted, error-free, or free of viruses or other harmful components. We do not warrant that results obtained through the Service will be accurate or reliable.

Nothing in the Service constitutes legal, compliance, or regulatory advice. You are responsible for ensuring your institution's compliance with all applicable laws and athletic governing body regulations.`,
  },
  {
    title: "9. Limitation of Liability",
    content: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ZEBISO INTELLIGENCE, INC. OR ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR THE USE OF OR INABILITY TO USE THE SERVICE.

OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE FEES PAID BY YOU IN THE 12 MONTHS PRECEDING THE CLAIM OR (B) $100 USD.`,
  },
  {
    title: "10. Termination",
    content: `You may cancel your subscription at any time through your account settings or by contacting support@zindeal.com. Cancellation takes effect at the end of the current billing period; no refunds are provided for unused time in a billing period.

We may terminate or suspend your access to the Service immediately, without prior notice, if you materially breach these Terms. Upon termination, your right to use the Service ceases immediately. Sections 4, 6, 7, 8, 9, and 11 survive termination.`,
  },
  {
    title: "11. Governing Law and Disputes",
    content: `These Terms are governed by the laws of the State of Delaware, without regard to its conflict of law provisions. Any dispute arising out of or relating to these Terms or the Service shall be resolved exclusively through binding arbitration administered by the American Arbitration Association under its Commercial Arbitration Rules, except that either party may seek injunctive or other equitable relief in a court of competent jurisdiction.

You waive any right to participate in a class action lawsuit or class-wide arbitration.`,
  },
  {
    title: "12. Changes to These Terms",
    content: `We may modify these Terms at any time. We will provide at least 30 days' notice of material changes via email to the address associated with your account. Your continued use of the Service after the effective date of any changes constitutes acceptance of the updated Terms.

If you do not agree to the modified Terms, you must stop using the Service and cancel your subscription before the effective date of the changes.`,
  },
  {
    title: "13. Contact",
    content: `If you have questions about these Terms, please contact us at:

Zebiso Intelligence, Inc.
legal@zindeal.com
support@zindeal.com`,
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      <nav className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Zindeal" className="w-8 h-8 rounded-full" />
            <span className="font-serif font-semibold text-lg tracking-tight">Zindeal</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/sign-in">Sign In</Link>
            </Button>
            <Button size="sm" className="bg-[#0B1834] hover:bg-[#0A152E] text-white" asChild>
              <Link href="/sign-up">Get Started</Link>
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 mb-3">Terms of Service</h1>
          <p className="text-gray-500 text-sm">Effective date: {EFFECTIVE_DATE}</p>
          <p className="mt-4 text-gray-600 leading-relaxed">
            Please read these Terms of Service carefully before using Zindeal. These Terms constitute a legally binding agreement between you and Zebiso Intelligence, Inc.
          </p>
        </div>

        <div className="space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-lg font-bold text-gray-900 mb-3">{section.title}</h2>
              <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {section.content}
              </div>
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-gray-100 py-8 px-6 mt-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Zindeal" className="w-6 h-6 rounded-full" />
            <span className="font-semibold text-gray-700">Zindeal</span>
          </div>
          <div className="flex gap-6">
            <Link href="/terms" className="hover:text-gray-700 transition-colors font-medium text-[#0B1834]">Terms of Service</Link>
            <Link href="/privacy" className="hover:text-gray-700 transition-colors">Privacy Policy</Link>
            <Link href="/subprocessors" className="hover:text-gray-700 transition-colors">Subprocessors</Link>
            <Link href="/sign-in" className="hover:text-gray-700 transition-colors">Sign In</Link>
          </div>
          <span>© {new Date().getFullYear()} Zebiso Intelligence, Inc. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
